import { useState } from "react";
import PropTypes from "prop-types";

import { Icon } from "@iconify/react";
import CompleteProfileModal from "./CompleteProfileModal.jsx";
import { Button, ButtonSize, ButtonVariant, PageLoader } from "@repo/design-system/components/ui";
import ProfileMainContent from "../feature/ProfileMainContent.jsx";

import {
  useGetProfileDashboardQuery,
  useGetSocialProfileLinkQuery,
  useLazyGetByIdSocialProfileLinkQuery,
  useAddSocialProfileLinkMutation,
  useUpdateSocialProfileLinkMutation,
  useDeleteSocialProfileLinkMutation,
} from "@gyandiksha/shared-ui/app/services";
import { ToastService, getFirstMiddleName } from "@gyandiksha/shared-ui";

import "./StudentProfile.scss";

const StudentProfile = ({
  profileData: initialProfileData,
  onCompleteProfile,
  onBack,
  onEditProfile,
  backButtonText = "Back",
}) => {

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: dashboardResponse, isLoading, refetch } = useGetProfileDashboardQuery();
  const { data: socialLinkResponse, refetch: refetchSocialLink } = useGetSocialProfileLinkQuery();
  const [getSocialLinkById] = useLazyGetByIdSocialProfileLinkQuery();
  const [addSocialProfileLink, { isLoading: isAddSocialLoading }] = useAddSocialProfileLinkMutation();
  const [updateSocialProfileLink, { isLoading: isUpdateSocialLoading }] = useUpdateSocialProfileLinkMutation();
  const [deleteSocialProfileLink, { isLoading: isDeleteSocialLoading }] = useDeleteSocialProfileLinkMutation();

  const profileData = dashboardResponse?.data || initialProfileData;
  const socialNetwork = socialLinkResponse?.data || [];
  const basic = profileData?.basicDetailsInfoDTO || {};
  const fullName = getFirstMiddleName(basic.fullName, basic.firstName, basic.lastName);

  const handleCompleteProfileClick = () => {
    setIsModalOpen(true);
    onCompleteProfile && onCompleteProfile();
  };

  const handleModalSave = async () => {
    await refetch();
  };

  const handleAddSocial = async (item) => {
    const response = await addSocialProfileLink({ name: item.name, url: item.url });
    if (response?.error) {
      return false;
    }
    ToastService.success({
      title: "Saved",
      description: `${item.name} profile link saved successfully`
    });
    refetchSocialLink();
    return true;
  };

  const handleEditSocial = async (item) => {
    const response = await updateSocialProfileLink({ urlId: item.id, name: item.name, url: item.url });
    if (response?.error) {
      return false;
    }
    ToastService.success({
      title: "Updated",
      description: `${item.name} profile link updated successfully`
    });
    refetchSocialLink();
    return true;
  };

  const handleFetchSocialById = async (id) => {
    const response = await getSocialLinkById(id);
    return response?.data?.data || null;
  };

  const handleDeleteSocial = async (item) => {
    const response = await deleteSocialProfileLink({ id: item.id });
    if (response?.error) {
      return false;
    }
    ToastService.success({
      title: "Deleted",
      description: `${item.name} profile link deleted successfully`
    });
    refetchSocialLink();
    return true;
  };

  return (
    <div className="student-profile">
      {isLoading ? <PageLoader /> :
        <>
          {/* ── Profile Completion Banner ── */}
          <div className="sp-completion-banner">
            <div className="sp-completion-icon">
              <Icon icon="solar:user-circle-bold-duotone" width={28} />
            </div>
            <div className="sp-completion-info">
              <h3>My Profile</h3>
              <p>View and manage your personal and academic details.</p>
            </div>
            <div className="sp-banner-actions">
              {onEditProfile && (
                <Button
                  variant={ButtonVariant.PRIMARY}
                  size={ButtonSize.SM}
                  leftIcon="material-symbols:edit-outline"
                  onClick={onEditProfile}
                >
                  Edit
                </Button>
              )}
              {onBack && (
                <Button
                  variant={ButtonVariant.OUTLINE}
                  size={ButtonSize.SM}
                  leftIcon="lucide:arrow-left"
                  className="sp-back-btn"
                  onClick={onBack}
                >
                  {backButtonText}
                </Button>
              )}
            </div>
            {/* {onCompleteProfile && (
              <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.SM}
                leftIcon="solar:pen-bold"
                className="sp-complete-btn"
                onClick={handleCompleteProfileClick}
              >
                Complete Profile
              </Button>
            )} */}
          </div>

          {/* ── Complete Profile Modal ── */}
          <CompleteProfileModal isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSave={handleModalSave}
            studentName={fullName}
          />

          {/* ── Main Content ── */}
          <ProfileMainContent
            profileData={profileData}
            socialNetwork={socialNetwork}
            onRefetchDashboard={refetch}
            onAddSocial={handleAddSocial}
            onEditSocial={handleEditSocial}
            onDeleteSocial={handleDeleteSocial}
            onFetchSocialById={handleFetchSocialById}
            isSavingSocial={isAddSocialLoading || isUpdateSocialLoading}
            isDeletingSocial={isDeleteSocialLoading}
          />
        </>
      }
    </div>
  );
};

StudentProfile.propTypes = {
  profileData: PropTypes.object,
  onCompleteProfile: PropTypes.func,
  onBack: PropTypes.func,
  onEditProfile: PropTypes.func,
  backButtonText: PropTypes.string,
};

export default StudentProfile;
